import { IncomingMessage, ServerResponse } from 'http'
import { v4 as uuidV4 } from 'uuid'
import { HEADER_KEYS, Iata } from '@reward-platform/shared-schemas'
import { getOpCoFromRequest } from '@reward-platform/platform-config/server'
import { getSession } from '@reward-platform/auth0-b2c'

import { GenericAPIException } from '@reward-platform/shared-api/exceptions/GenericAPIException'
import { FeatureFlagKey } from '@reward-platform/devcycle'
import { DevCycleUser, DevCycleVariableResponseMap } from './devcycle.types'
import { devcycleApiClient } from './devcycle.client'
import { getDevCycleHeaders } from './devcycle.service'

const UNAUTHENTICATED_DVC_USER_ID_PREFIX = 'anon'

const PARTNER_DOMAIN_MAP: Record<Iata, string> = {
  IB: 'iberia',
  EI: 'aerlingus',
  BA: 'britishairways',
} as const

const getDevcycleUserFromSession = async (
  req: IncomingMessage,
  res: ServerResponse<IncomingMessage>,
  commonData: Partial<DevCycleUser>
) => {
  const session = await getSession(req, res)
  if (!session) {
    throw Error('Error in getSession, using default user')
  }
  const userId = session.user.sub.split('|')[1]

  return {
    user_id: userId,
    email: session.user.email,
    ...commonData,
  }
}

export const getDevcycleUserFromRequest = async (
  req: IncomingMessage,
  res: ServerResponse<IncomingMessage>
): Promise<DevCycleUser> => {
  const opCo = getOpCoFromRequest(req)
  // NOTE: partner uses 'domain' format of key, will be moving to Iata in future so this will need to be removed
  const customData = { partner: PARTNER_DOMAIN_MAP[opCo], opCo }
  const commonData: Partial<DevCycleUser> = { customData, sdkType: 'api' }

  try {
    return await getDevcycleUserFromSession(req, res, commonData)
  } catch {
    const correlationId = req.headers[HEADER_KEYS.correlationId] as string
    const userId = `${UNAUTHENTICATED_DVC_USER_ID_PREFIX}-${correlationId ?? uuidV4()}`
    return {
      isAnonymous: true,
      user_id: userId,
      ...commonData,
    }
  }
}

export const getGenericFeatureFlag = async (flagKey: FeatureFlagKey, defaultValue = false) => {
  const commonData: Partial<DevCycleUser> = { sdkType: 'api' }
  const user = {
    isAnonymous: true,
    user_id: `${UNAUTHENTICATED_DVC_USER_ID_PREFIX}-${uuidV4()}`,
    ...commonData,
  }

  try {
    const response = await devcycleApiClient.post<DevCycleVariableResponseMap>('/variables', user, {
      headers: getDevCycleHeaders(),
    })
    return response.data[flagKey]?.value ?? defaultValue
  } catch (error: any) {
    throw new GenericAPIException(error)
  }
}
